"use client";

import { useBreakpoint } from "@/utils/useBreakpoint";
import { useScrollVisibility } from "@/utils/useScrollVisibility";
import { motion } from "framer-motion";
import React, { useState, useEffect } from "react";
import { getNextZIndex } from "@/utils/globalZIndex";
import { setGlobalRotation } from "@/utils/globalState";

// Variants
export const useBreakpointVariants = (
  zIndex: number,
  transitionTime: number = 1400,
  variants: any,
  useExtraPosition: boolean,
  position: string,
  withZIndex: boolean
) => {
  const duration = transitionTime / 1000; // Convert to seconds for framer-motion
  const breakpoint = useBreakpoint();
  const sectionVariants = {
    desktop: {
      visible: {
        zIndex: getNextZIndex(),
        transition: {
          duration,
          ease: "easeInOut",
        },
      },
      above: {
        zIndex: zIndex,
        transition: { duration, ease: "easeInOut" },
      },
      below: {
        zIndex: zIndex,
        transition: { duration, ease: "easeInOut" },
      },
    },
    mobile: {
      visible: {
        zIndex: getNextZIndex(),
        transition: { duration, ease: "easeInOut" },
      },
      above: {
        zIndex: zIndex,
        transition: { duration, ease: "easeInOut" },
      },
      below: {
        zIndex: zIndex,
        transition: { duration, ease: "easeInOut" },
      },
    },
  };

  const mergedVariants = {
    desktop: {
      visible: {
        ...sectionVariants.desktop.visible,
        ...variants?.desktop?.visible,
      },
      above: {
        ...sectionVariants.desktop.above,
        ...variants?.desktop?.above,
      },
      below: {
        ...sectionVariants.desktop.below,
        ...variants?.desktop?.below,
      },
      outside: {
        ...variants?.desktop?.outside,
      },
    },
    mobile: {
      visible: {
        ...sectionVariants.mobile.visible,
        ...variants?.mobile?.visible,
      },
      above: {
        ...sectionVariants.mobile.above,
        ...variants?.mobile?.above,
      },
      below: {
        ...sectionVariants.mobile.below,
        ...variants?.mobile?.below,
      },
      outside: {
        ...variants?.mobile?.outside,
      },
    },
  };

  // Remove outside variants if useExtraPosition is false
  if (!useExtraPosition) {
    delete mergedVariants.desktop.outside;
    delete mergedVariants.mobile.outside;
  }

  if (!withZIndex) {
    // Remove zIndex from all desktop variants
    if (mergedVariants.desktop.visible?.zIndex)
      delete mergedVariants.desktop.visible.zIndex;
    if (mergedVariants.desktop.above?.zIndex)
      delete mergedVariants.desktop.above.zIndex;
    if (mergedVariants.desktop.below?.zIndex)
      delete mergedVariants.desktop.below.zIndex;
    if (mergedVariants.desktop.outside?.zIndex)
      delete mergedVariants.desktop.outside.zIndex;

    // Remove zIndex from all mobile variants
    if (mergedVariants.mobile.visible?.zIndex)
      delete mergedVariants.mobile.visible.zIndex;
    if (mergedVariants.mobile.above?.zIndex)
      delete mergedVariants.mobile.above.zIndex;
    if (mergedVariants.mobile.below?.zIndex)
      delete mergedVariants.mobile.below.zIndex;
    if (mergedVariants.mobile.outside?.zIndex)
      delete mergedVariants.mobile.outside.zIndex;
  }

  return mergedVariants[breakpoint];
};

// Animation Component
interface AnimationProps {
  children: React.ReactNode;
  zIndex?: number;
  thresholds?: number[]; // Array of 2 numbers: [show, hide]
  mobileThresholds?: number[];
  transitionTime?: number;
  section?: keyof typeof motion;
  useExtraPosition?: boolean;
  withZIndex?: boolean;
  onAnimationStateChange?: (state: string) => void;
  helperRotation?: number;
}

const AnimatedSection = ({
  children,
  zIndex,
  className,
  thresholds = [0.5, 1],
  mobileThresholds = [0.5, 1],
  transitionTime = 1400, // Provide default value
  section = "section",
  variants,
  useExtraPosition = false,
  withZIndex = false,
  onAnimationStateChange = () => {},
  helperRotation = 999,
}: AnimationProps & { className?: string; variants?: any }) => {
  const breakpoint = useBreakpoint();
  const position = useScrollVisibility({
    animationThresholds:
      breakpoint === "mobile" ? mobileThresholds : thresholds,
    useExtraPosition: useExtraPosition,
  });

  // State for scroll direction
  const [scrollDir, setScrollDir] = useState<"up" | "down">("down");
  const [lastScrollY, setLastScrollY] = useState(0);

  // Effect to track scroll direction
  useEffect(() => {
    const updateScrollDir = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY) {
        setScrollDir("down");
      } else if (currentScrollY < lastScrollY) {
        setScrollDir("up");
      }

      setLastScrollY(currentScrollY);
    };

    const onScroll = () => {
      window.requestAnimationFrame(updateScrollDir);
    };

    window.addEventListener("scroll", onScroll);

    return () => window.removeEventListener("scroll", onScroll);
  }, [lastScrollY]);

  // Call onAnimationStateChange when position changes
  useEffect(() => {
    if (onAnimationStateChange) {
      onAnimationStateChange(position);
    }
  }, [position, onAnimationStateChange]);

  // Rotation for helper
  useEffect(() => {
    if (position === "visible" && helperRotation !== 999) {
      if (scrollDir === "down") {
        setGlobalRotation(helperRotation);
      } else {
        setGlobalRotation(helperRotation + 180);
      }
    }
  }, [position, helperRotation, scrollDir]);

  const allVariants = useBreakpointVariants(
    zIndex || 0,
    transitionTime,
    variants,
    useExtraPosition,
    position,
    withZIndex
  );

  const MotionComponent = motion[section];

  return (
    <MotionComponent
      style={{ zIndex }}
      className={className}
      initial="visible"
      animate={position}
      variants={allVariants}
    >
      {children}
    </MotionComponent>
  );
};

export default AnimatedSection;
